/* ForecastingCentrePage.jsx — iter-244 · Forecasting Centre pilot.
 *
 * Evolves the /sales-progression route into the canonical
 * Forecasting Centre experience. Calm, executive-grade
 * forecasting infrastructure — Apple/Stripe/Linear-inspired.
 *
 * NOT a CRM dashboard. NOT analytics overload. This page reads
 * as quiet operational telemetry: what's expected to exchange
 * and the revenue the operator can bank.
 *
 * Routing:
 *   - /sales-progression  → this page (existing route preserved)
 *   - /forecasting-centre → this page (new canonical route)
 *
 * Doctrine: Reduce > Add · Calm > Stimulate · Resolve > Display.
 */
import React, { useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Upload, Calendar, Filter, Bell, TrendingUp, AlertTriangle,
  ShieldCheck, CalendarCheck, MoreHorizontal, ChevronRight,
  ClipboardList, MessageSquare, Search, FileCheck,
} from "lucide-react";

import { Sidebar } from "../components/Sidebar";
import ShellCanvas from "../components/layout/ShellCanvas";
import { PageHeader, WORKSPACE_SHELL_CLASS } from "../components/ui/PageHeader";
import { useChainStore } from "../data/chainStore";
import { onPropertyImgError, PROPERTY_FALLBACK_SVG } from "../lib/propertyImage";
import { resolveAgreedFeeGBP } from "../lib/feeFormat";
/* iter-283 — Workstream #3: Forecasting Centre integration.
 *
 * The Forecasting Centre now consumes the iter-281 Deal Health
 * scoring engine so per-deal confidence + the "On track / Watch
 * / At risk" labels read off the same source of truth as the
 * Deal Rooms list. When the iter-282 Documents → Timeline
 * automation clears blockers, the operational pressure rail
 * also recomputes (resolved blockers are skipped).
 *
 * Flag-gated via the existing iter-281 `REACT_APP_HEALTH_SCORING_V1`
 * environment variable — flipping that single flag promotes the
 * scoring engine from "computed but not surfaced" to "the
 * authoritative confidence number". Default OFF in preview.
 */
import { scoreDeal, isHealthScoringEnabled, HEALTH_BANDS } from "../data/dealHealth";
import { needsAttentionDeal } from "../data/dealFilters";
/* iter-285 — Participant accountability extension. When enabled,
 * the Forecasting Centre's confidence number reads the iter-281
 * scorer WITH the iter-285 participant signal envelope, so silent
 * solicitors / brokers register against the forecast directly. */
import {
  participantSignalsFor,
  isParticipantAccountabilityEnabled,
} from "../data/participants";

const FALLBACK_THUMB = PROPERTY_FALLBACK_SVG;

const STAGE_LABEL = {
  offer: "Offer", aml: "AML", memo: "Memo", solicitors: "Solicitors",
  mortgage_app: "Mortgage", survey: "Survey", searches: "Searches",
  enquiries: "Enquiries", mortgage_offer: "Mortgage",
  exchange_ready: "Contracts", exchanged: "Exchanged", completed: "Completed",
};
const STAGE_DOT = {
  offer: "bg-slate-400", aml: "bg-amber-500", memo: "bg-amber-500",
  solicitors: "bg-indigo-500", mortgage_app: "bg-violet-500",
  mortgage_offer: "bg-violet-500", survey: "bg-sky-500",
  searches: "bg-orange-500", enquiries: "bg-orange-500",
  exchange_ready: "bg-violet-600", exchanged: "bg-emerald-500", completed: "bg-emerald-600",
};

const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return "£0";
  const abs = Math.abs(n);
  if (abs >= 1000) return `£${(n / 1000).toFixed(abs >= 100000 ? 0 : 1).replace(/\.0$/, "")}k`;
  return `£${Math.round(n).toLocaleString()}`;
};
const fmtMoneyFull = (n) => {
  if (n == null || isNaN(n)) return "£0";
  return `£${Math.round(n).toLocaleString()}`;
};

const _parseFee = (raw) => {
  if (typeof raw === "number") return raw;
  if (!raw) return 0;
  const num = parseFloat(String(raw).replace(/[^0-9.]/g, ""));
  return isNaN(num) ? 0 : num;
};
const _parsePrice = (raw) => _parseFee(raw);
/* iter-299 — Live Fee + VAT resolver.
 *
 * Single source of truth for the gross commission value displayed
 * across the Forecasting Centre.  Reads directly from the same
 * deal envelope used by Deal Rooms and transaction timelines.
 *
 * iter-alignment · P0 · Agreed Fee Basis Reconciliation
 * (2026-02-15) — the NET-of-VAT £ is now resolved via the
 * shared `resolveAgreedFeeGBP(deal)` SSOT so percentage deals
 * ALWAYS compute `agreedPrice × feePct` (Fee Basis Integrity
 * clears the stored fixed fee on every percentage save; the
 * legacy fallback of `_parseFee(d.fee) > 0` used to short-
 * circuit fixed-mode deals but silently returned 0 for the
 * percentage rows).  Forecasting still grosses up to Fee + VAT
 * so bar heights, banked revenue and at-risk sums stay
 * consistent with the operator brief.
 *
 * No synthetic calibration overrides. Any change to a deal's fee,
 * feePct or agreedPrice in Deal Rooms re-flows here instantly. */
const _feeFor = (d) => {
  if (!d) return 0;
  const net = resolveAgreedFeeGBP(d);
  if (net == null || net <= 0) return 0;
  // Blank VAT flag reads "+ VAT", same as the Revenue card; only an explicit "VAT included" skips gross-up.
  const grossed = d.vatIncluded === true ? net : Math.round(net * 1.20);
  return Math.round(grossed);
};

// ─── Live forecast vs banked partitioning ───────────────────────────
//
// iter-299 — Deal Rooms is the single source of truth. Every deal
// in chainStore flows through these helpers unmodified — no
// synthetic profiles, no calibrated overrides, no per-office
// massaging. Status / fee / target-date edits in any Deal Room
// reflect here on the next render.
//
//   • Forecast pool: deals whose stage is not `completed` and
//     whose target exchange date falls within the visible
//     6-month horizon. Already-exchanged deals stay anchored to
//     their actual exchange month (per the iter-299 spec).
//   • Banked pool: deals whose stage === `completed`. Their Fee +
//     VAT contributes to "Banked revenue" only — they no longer
//     count toward forecast totals.
//   • Monthly "exchange count" badge: a count of deals whose
//     stage === `exchanged` AND whose exchange month matches the
//     bar. Pre-exchange deals are forecast volume, exchanged
//     deals are realised volume.
const isExchanged = (d) => d?.stage === "exchanged";
// Fee is recognised (banked) at exchange; completion never re-banks it or moves its month.
const isBanked = (d) => d?.stage === "exchanged" || d?.stage === "completed";
const isForecastEligible = (d) => !!d && !isBanked(d);
// Banked month = actual exchange date (Confirm Exchange audit); never a target or completion date.
const bankedDateFor = (d) => d.exchangedAt || d.exchanged_at || null;

const monthKey = (d) => {
  if (!d) return null;
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    if (isNaN(dt)) return null;
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
  } catch { return null; }
};
const monthLabel = (key) => {
  if (!key) return "—";
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-GB", { month: "short" });
};
const monthLong = (key) => {
  if (!key) return "—";
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-GB", { month: "long" });
};

// Hash-based deterministic confidence per deal so cards stay stable
// across renders.
//
// iter-283 — Forecasting Centre integration. When the iter-281
// health scoring flag is enabled, the iter-281 scorer becomes the
// authoritative confidence number for every forecast surface in
// this page. The fallback ladder (`exchangeProbability` →
// `dealHealthScore` → stage-weighted base) remains in place for
// non-flagged renders and for legacy deals carrying explicit
// probability fields.
const confidenceFor = (d) => {
  // iter-283 — Health scorer is the canonical source when the
  // flag is on. We pass `null` for ops because the scorer derives
  // every signal it needs directly from the deal envelope (waitDays,
  // overdueDays, blockers, completedSteps, tenure, etc.).
  //
  // iter-285 — When participant accountability is also enabled,
  // we additionally pass a participantSignals envelope so the
  // forecast confidence reflects silent solicitor / broker.
  if (isHealthScoringEnabled()) {
    let _opsForScorer = null;
    if (isParticipantAccountabilityEnabled()) {
      const _requests = Array.isArray(d.docRequests) ? d.docRequests : [];
      _opsForScorer = { participantSignals: participantSignalsFor({ deal: d, requests: _requests }) };
    }
    const r = scoreDeal(d, _opsForScorer);
    if (r && typeof r.score === "number") return r.score;
  }
  if (typeof d.exchangeProbability === "number") return Math.max(0, Math.min(100, Math.round(d.exchangeProbability)));
  if (typeof d.exchange_probability === "number") return Math.max(0, Math.min(100, Math.round(d.exchange_probability)));
  // Canonical only (exchange_probability); null when the deal has none — never a stage guess or hash jitter.
  return null;
};
// iter-283 — Band cutoffs harmonised with iter-281's HEALTH_BANDS
// (HEALTHY_MIN: 70, AT_RISK_MAX: 39). When the health scoring flag
// is OFF the page falls back to the legacy 85/70 cutoffs so existing
// pilot screenshots stay byte-identical.
const confidenceState = (pct) => {
  if (isHealthScoringEnabled()) {
    if (pct >= HEALTH_BANDS.HEALTHY_MIN)      return { label: "On track",        cls: "text-emerald-600" };
    if (pct >  HEALTH_BANDS.AT_RISK_MAX)      return { label: "Needs attention", cls: "text-amber-600" };
    return                                            { label: "At risk",         cls: "text-rose-600" };
  }
  if (pct >= 85) return { label: "On track",       cls: "text-emerald-600" };
  if (pct >= 70) return { label: "Needs attention", cls: "text-amber-600" };
  return            { label: "At risk",            cls: "text-rose-600" };
};

const operationalPressureFor = (deals) => {
  // iter-299 — Live operational pressure rail.
  // Sources rows directly from chainStore deals: no synthetic
  // floors, no per-bucket minimum counts, no fee ceilings.
  // Maps the canonical `revenueAtRisk` (Low/Medium/High) onto
  // the legacy `risk` field so existing match predicates still
  // resolve without an extra adapter.
  const annotated = (deals || []).map((d) => ({
    ...d,
    risk: d.risk || d.revenueAtRisk || null,
  }));
  // A deal counts toward a blocker-driven bucket ONLY when at
  // least one of its blockers is still unresolved. iter-282's
  // documents → timeline automation can mark blockers
  // `status: "resolved"`, lifting pressure on the next render.
  const _hasUnresolvedBlocker = (d) => {
    const blockers = Array.isArray(d.blockers) ? d.blockers : [];
    if (blockers.length === 0) return true;       // no blocker data → keep legacy behaviour
    return blockers.some((b) => b && b.status !== "resolved");
  };
  const buckets = [
    { key: "mortgage_offers_expiring", label: "Mortgage offers expiring", icon: CalendarCheck, tone: "rose",   match: (d) => d.stage === "mortgage_offer" && d.risk === "Medium" },
    { key: "solicitor_delays",         label: "Solicitor delays",         icon: MessageSquare, tone: "amber",  match: (d) => (d.stage === "solicitors" || d.stage === "enquiries") && (d.risk === "Medium" || d.risk === "High") && _hasUnresolvedBlocker(d) },
    { key: "searches_outstanding",     label: "Searches outstanding",     icon: Search,        tone: "amber",  match: (d) => d.stage === "searches" && _hasUnresolvedBlocker(d) },
    { key: "survey_issues",            label: "Survey issues",            icon: FileCheck,     tone: "rose",   match: (d) => d.stage === "survey" && (d.risk === "Medium" || d.risk === "High") && _hasUnresolvedBlocker(d) },
  ];
  return buckets.map((b) => {
    const rows = annotated.filter(b.match);
    const atRisk = rows.reduce((sum, d) => sum + _feeFor(d), 0);
    // iter-301 — Expose the deal IDs that contribute to each
    // bucket so clicking the pressure row can deep-link into
    // Deal Rooms filtered to exactly those deals.
    const dealIds = rows.map((d) => d.id);
    return { ...b, count: rows.length, atRisk, dealIds };
  });
};

const TONE_STYLES = {
  rose:    { bg: "bg-rose-50",    fg: "text-rose-500",    money: "text-rose-600",    sub: "text-rose-500" },
  amber:   { bg: "bg-amber-50",   fg: "text-amber-500",   money: "text-amber-600",   sub: "text-amber-500" },
  violet:  { bg: "bg-violet-50",  fg: "text-violet-500",  money: "text-violet-700",  sub: "text-violet-500" },
};

/* ─── KPI Card ─────────────────────────────────────────────────────── */
/* iter-301 — KPI cards are actionable.
 *
 * When `onClick` is provided the card renders as a <button>,
 * gains a calm hover affordance + focus ring, and deep-links the
 * operator into Deal Rooms filtered to the exact deal IDs that
 * contributed to the KPI. The static, non-interactive shape is
 * preserved for the unlikely case of a display-only card. */
const KpiCard = ({ label, value, sub, accent, icon: Icon, testid, onClick, ariaLabel }) => {
  // Fixed rhythm on every card: heading → value → subtitle slot (always
  // reserved, even when empty) so all three share baselines; icon pinned.
  const inner = (
    <div className="relative h-full flex flex-col pr-14">
      <p className="text-[12px] font-semibold text-slate-500 tracking-tight" data-testid={`${testid}-label`}>{label}</p>
      <p className="mt-2 text-[28px] font-bold text-slate-900 leading-none tracking-tight tabular-nums" data-testid={`${testid}-value`}>{value}</p>
      <div className="mt-2 min-h-[18px] text-[11.5px] leading-[18px]" data-testid={`${testid}-sub`}>{sub}</div>
      {Icon && (
        <div className={`absolute top-0 right-0 w-10 h-10 rounded-xl flex items-center justify-center ${accent || "bg-slate-50"}`} data-testid={`${testid}-icon`}>
          <Icon size={18} strokeWidth={2} className={(accent && accent.includes("emerald")) ? "text-emerald-600" : (accent && accent.includes("amber") ? "text-amber-600" : (accent && accent.includes("rose") ? "text-rose-600" : "text-slate-600"))} />
        </div>
      )}
    </div>
  );
  if (!onClick) {
    return (
      <div
        className="rounded-2xl bg-white p-5 ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
        data-testid={testid}
      >
        {inner}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel || label}
      className="text-left rounded-2xl bg-white p-5 ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:ring-violet-300/60 hover:shadow-[0_4px_16px_-6px_rgba(124,58,237,0.18)] focus:outline-none focus:ring-2 focus:ring-violet-400/60 transition"
      data-testid={testid}
    >
      {inner}
    </button>
  );
};

/* ─── Forecast bar chart ───────────────────────────────────────────── */
const ForecastChart = ({ data }) => {
  // Defensive — guard against an empty, undefined or all-null data
  // pipeline so the chart NEVER renders into a collapsed shell.
  const safeData = Array.isArray(data) && data.length > 0 ? data : [];
  // Coerce revenue + count to numeric primitives BEFORE any math.
  // Formatted currency strings ("£3.3k") would silently NaN out of
  // the height calculation and produce invisible bars.
  const normalised = safeData.map((d) => ({
    key:     d?.key   ?? "",
    count:   Number.isFinite(d?.count)   ? d.count   : 0,
    total:   Number.isFinite(d?.total)   ? d.total   : (Number.isFinite(d?.count) ? d.count : 0),
    revenue: Number.isFinite(d?.revenue) ? d.revenue : 0,
  }));
  const revenues = normalised.map((d) => d.revenue);
  const rawMax = Math.max(0, ...revenues);
  // Safe fallback when every month is zero — chart still renders
  // labels + circles without throwing on divide-by-zero.
  const maxRevenue = rawMax > 0 ? rawMax : 1;
  // Round Y-axis ceiling to a calm step so labels read cleanly.
  const niceTop = (() => {
    if (rawMax <= 0)      return 20000;
    if (rawMax <= 20000)  return 20000;
    if (rawMax <= 50000)  return 50000;
    if (rawMax <= 100000) return 100000;
    if (rawMax <= 200000) return 200000;
    if (rawMax <= 400000) return 400000;
    if (rawMax <= 600000) return 600000;
    if (rawMax <= 800000) return 800000;
    if (rawMax <= 1000000) return 1000000;
    return Math.ceil(rawMax / 200000) * 200000;
  })();
  const ticks = [niceTop, niceTop * 0.75, niceTop * 0.5, niceTop * 0.25, 0];

  // Bar height (as % of the chart plot area). `Math.max(…, 4)` keeps
  // small non-zero values visibly rendered; zero months render as
  // truly empty so they don't read as misleading thin bars.
  const heightPctFor = (rev) => {
    if (!Number.isFinite(rev) || rev <= 0) return 0;
    return Math.max(4, (rev / niceTop) * 100);
  };

  return (
    <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] h-full" data-testid="forecast-chart">
      <div className="flex items-baseline justify-between mb-6">
        <h3 className="text-[15px] font-bold tracking-tight text-slate-900">Forecasted revenue by month</h3>
        <span className="text-[11.5px] font-semibold text-slate-400">Count = deals · Bar = Fee + VAT</span>
      </div>
      <div className="relative h-[320px] flex">
        {/* Y-axis labels */}
        <div className="flex flex-col justify-between text-[10.5px] font-semibold text-slate-400 pr-3 py-1 w-12 text-right">
          {ticks.map((t) => (
            <span key={t}>£{Math.round(t / 1000)}k</span>
          ))}
        </div>
        {/* Plot area */}
        <div className="flex-1 relative">
          {/* Gridlines */}
          {ticks.map((t, i) => (
            <div
              key={t}
              className="absolute left-0 right-0 border-t border-slate-100"
              style={{ top: `${(i / (ticks.length - 1)) * 100}%` }}
            />
          ))}
          {/* Column rail — one full-height column per month so
              percentage heights resolve against the plot area. */}
          <div className="absolute inset-0 flex items-stretch justify-around gap-3 px-2">
            {normalised.map((d, i) => {
              const heightPct = heightPctFor(d.revenue);
              return (
                <div
                  key={d.key || i}
                  className="flex-1 h-full flex flex-col justify-end items-center"
                  data-testid={`forecast-bar-${i}`}
                  data-revenue={d.revenue}
                  data-height-pct={heightPct.toFixed(2)}
                >
                  {/* Count circle — sits just above the bar */}
                  <div
                    className="w-6 h-6 rounded-full bg-violet-50 ring-1 ring-violet-200 flex items-center justify-center mb-1.5"
                    data-testid={`forecast-bar-count-${i}`}
                  >
                    <span className="text-[10.5px] font-bold text-violet-700 tabular-nums">{d.count}</span>
                  </div>
                  {/* Revenue label */}
                  <span
                    className="text-[11.5px] font-bold text-slate-900 tabular-nums mb-1.5"
                    data-testid={`forecast-bar-revenue-${i}`}
                  >
                    {fmtMoney(d.revenue)}
                  </span>
                  {/* Bar — rises from the baseline. `height: X%`
                      now resolves against the full-height column,
                      so non-zero values render visibly. */}
                  <div
                    className="w-full max-w-[56px] rounded-t-md bg-gradient-to-b from-violet-500 to-violet-600 transition-[height] duration-500"
                    style={{ height: `${heightPct}%` }}
                    data-testid={`forecast-bar-fill-${i}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* X labels */}
      <div className="flex pl-12 mt-3">
        <div className="flex-1 flex justify-around gap-3 px-2">
          {normalised.map((d) => (
            <div key={d.key} className="flex-1 text-center">
              <span className="text-[12px] font-semibold text-slate-500">{monthLabel(d.key)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Forecast table row ───────────────────────────────────────────── */
const ForecastRow = ({ deal, propertyById, onOpen, index, prefix = "forecast-row", overdue = false }) => {
  const prop = propertyById.get(deal.propertyId) || {};
  const addr = (prop.address || deal.name || "").split(",");
  const street = addr[0] || "Unknown property";
  const area = addr.slice(1).join(",").trim() || (prop.postcode || "");
  const fee = _feeFor(deal);
  const pct = confidenceFor(deal);
  const _baseState = pct == null ? { label: "Not assessed", cls: "text-slate-400" } : confidenceState(pct);
  const stage = STAGE_LABEL[deal.stage] || "—";
  const stageDot = STAGE_DOT[deal.stage] || "bg-slate-400";
  const exchangeDate = deal.targetExchange || deal.target_exchange;
  const _today = new Date(); _today.setHours(0, 0, 0, 0);
  const isOverdue = overdue || (!isBanked(deal) && !!exchangeDate
    && Date.parse(String(exchangeDate).slice(0, 10) + "T00:00:00") < _today.getTime());
  // An overdue target can never read "On track"; the % stays as the likelihood score.
  const state = isOverdue ? { label: "Target overdue", cls: "text-rose-600" } : _baseState;
  return (
    <tr className="border-b border-slate-100/80 last:border-b-0 hover:bg-slate-50/40 transition-colors" data-testid={`${prefix}-${index}`}>
      <td className="py-3.5 pl-4 pr-3">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={prop.thumb || FALLBACK_THUMB}
            onError={onPropertyImgError}
            alt=""
            className="w-10 h-10 rounded-lg object-cover ring-1 ring-slate-200 shrink-0"
          />
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-slate-900 truncate">{street}</p>
            <p className="text-[11.5px] text-slate-500 truncate">{area || prop.postcode || "—"}</p>
          </div>
        </div>
      </td>
      <td className={`py-3.5 px-3 text-[12.5px] tabular-nums whitespace-nowrap ${isOverdue ? "text-rose-600 font-semibold" : "text-slate-600"}`}>
        {isOverdue && <span className="mr-1" data-testid={`${prefix}-overdue-${index}`}>Overdue ·</span>}
        {exchangeDate ? new Date(exchangeDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
      </td>
      <td className="py-3.5 px-3 text-[12.5px] font-semibold text-slate-900 tabular-nums whitespace-nowrap">{fmtMoneyFull(fee)}</td>
      <td className="py-3.5 px-3 whitespace-nowrap">
        <p className={`text-[13px] font-bold ${state.cls} tabular-nums`}>{pct == null ? "—" : `${pct}%`}</p>
        <p className={`text-[11px] font-medium ${state.cls}`}>{state.label}</p>
      </td>
      <td className="py-3.5 px-3 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-600">
          <span className={`w-1.5 h-1.5 rounded-full ${stageDot}`} />
          {stage}
        </span>
      </td>
      <td className="py-3.5 px-3 pr-4 whitespace-nowrap">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onOpen(deal.id)}
            className="h-8 px-3 rounded-lg text-[12px] font-semibold text-slate-700 ring-1 ring-slate-200 bg-white hover:bg-slate-50 transition-colors"
            data-testid={`${prefix}-open-${index}`}
          >
            Open deal
          </button>
          <button
            type="button"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="More actions"
            data-testid={`forecast-row-more-${index}`}
          >
            <MoreHorizontal size={15} strokeWidth={2} />
          </button>
        </div>
      </td>
    </tr>
  );
};

/* ─── Operational pressure row ─────────────────────────────────────── */
/* iter-301 — Each pressure row is a deep link into Deal Rooms
 * filtered to exactly the deals contributing to that bucket. */
const PressureRow = ({ row, isLast, onClick }) => {
  const tone = TONE_STYLES[row.tone] || TONE_STYLES.amber;
  const Icon = row.icon;
  // Shared 4-col grid (icon · label · amount · chevron), reused by the total row.
  const inner = (
    <>
      <div className={`w-9 h-9 rounded-xl ${tone.bg} flex items-center justify-center`}>
        <Icon size={15} strokeWidth={2} className={tone.fg} />
      </div>
      <div className="min-w-0 text-left">
        <p className="text-[12.5px] font-semibold text-slate-900 leading-tight">{row.label}</p>
        <p className="text-[11px] text-slate-500 mt-0.5">{row.count} {row.count === 1 ? "deal" : "deals"}</p>
      </div>
      <div className="text-right">
        <p className={`text-[12.5px] font-bold ${tone.money} tabular-nums`}>{fmtMoneyFull(row.atRisk)}</p>
        <p className={`text-[11px] ${tone.sub}`}>at risk</p>
      </div>
      <ChevronRight size={16} strokeWidth={2} className="text-slate-300" aria-hidden="true" />
    </>
  );
  const interactive = !!onClick && row.count > 0;
  if (!interactive) {
    return (
      <div
        className={`grid grid-cols-[36px_minmax(0,1fr)_104px_16px] items-center gap-[18px] py-3.5 ${isLast ? "" : "border-b border-slate-100"}`}
        data-testid={`pressure-${row.key}`}
      >
        {inner}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open ${row.label} in Deal Rooms`}
      className={`w-full grid grid-cols-[36px_minmax(0,1fr)_104px_16px] items-center gap-[18px] py-3.5 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-400/60 transition-colors ${isLast ? "" : "border-b border-slate-100"}`}
      data-testid={`pressure-${row.key}`}
    >
      {inner}
    </button>
  );
};

/* ─── Main page ────────────────────────────────────────────────────── */
/* iter-299B — Filter range resolver.
 *
 * Each filter resolves to a contiguous `[start, end]` pair of
 * `YYYY-MM` keys. Bars, KPIs, the table dropdown, the export and
 * the pressure rail all derive their visible deal subset from
 * this single range, so every chart/KPI/total recalculates in
 * lockstep when the operator switches the dropdown. */
const FILTER_OPTIONS = [
  { value: "this",      label: "This month" },
  { value: "next",      label: "Next month" },
  { value: "quarter",   label: "This quarter" },
  { value: "next_q",    label: "Next quarter" },
  { value: "ytd",       label: "Year to date" },
  { value: "next6",     label: "Next 6 months" },
  { value: "next12",    label: "Next 12 months" },
  { value: "all",       label: "All time" },
];

const _ym = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;
const _addMonths = (y, m, n) => {
  const dt = new Date(y, m + n, 1);
  return [dt.getFullYear(), dt.getMonth()];
};
const _quarterStart = (m) => Math.floor(m / 3) * 3;

const monthSeriesFor = (filter, now, allKeys) => {
  const y = now.getFullYear();
  const m = now.getMonth();
  const span = (yy, mm, count) => {
    const out = [];
    for (let i = 0; i < count; i++) {
      const [ny, nm] = _addMonths(yy, mm, i);
      out.push(_ym(ny, nm));
    }
    return out;
  };
  switch (filter) {
    case "this":    return [_ym(y, m)];
    case "next":    { const [ny, nm] = _addMonths(y, m, 1); return [_ym(ny, nm)]; }
    case "quarter": { const qs = _quarterStart(m); return span(y, qs, 3); }
    case "next_q":  { const [ny, nm] = _addMonths(y, _quarterStart(m), 3); return span(ny, nm, 3); }
    case "ytd":     return span(y, 0, m + 1);
    case "next12":  return span(y, m, 12);
    case "all": {
      // Union of every month that carries at least one live deal
      // (forecast + banked). Sorted chronologically; capped at 18
      // bars so the chart still reads cleanly on extreme data.
      const sorted = Array.from(allKeys).sort();
      if (sorted.length === 0) return span(y, m, 6);
      // Always include the current month so a fresh workspace
      // still renders a sensible chart.
      if (!sorted.includes(_ym(y, m))) sorted.push(_ym(y, m));
      sorted.sort();
      return sorted.slice(-18);
    }
    case "next6":
    default:        return span(y, m, 6);
  }
};

const ForecastingCentrePage = () => {
  const navigate = useNavigate();
  const { deals = [], properties = [], isPropertyArchived } = useChainStore() || {};
  const [monthFilter, setMonthFilter] = useState("next6");

  const propertyById = useMemo(() => {
    const m = new Map();
    for (const p of properties) m.set(p.id, p);
    return m;
  }, [properties]);

  const now = useMemo(() => new Date(), []);
  const _currentMonthKey = useMemo(
    () => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
    [now],
  );

  // iter-299 — Live forecast pool. Sources every deal that:
  //   • is not archived (archived inventory hidden from forecast);
  //   • is not yet completed (completed deals move to Banked).
  // Already-exchanged deals stay in the pool so they continue to
  // anchor their realised exchange month on the chart.
  const forecastDeals = useMemo(() => {
    return deals.filter((d) => {
      if (isBanked(d)) return false;
      if (isPropertyArchived?.(d.propertyId || d.property_id)) return false;
      return true;
    });
  }, [deals, isPropertyArchived]);

  // iter-299 — Banked pool. Completed deals only — their Fee+VAT
  // moves out of forecast revenue and into the Banked Revenue
  // surface.
  const bankedDeals = useMemo(() => {
    return deals.filter((d) => {
      if (!isBanked(d)) return false;
      if (isPropertyArchived?.(d.propertyId || d.property_id)) return false;
      return true;
    });
  }, [deals, isPropertyArchived]);

  // Union of months carrying live activity — required for the
  // "All time" filter so the chart auto-fits the real data spread.
  const liveMonthKeys = useMemo(() => {
    const set = new Set();
    for (const d of forecastDeals) {
      const k = monthKey(d.targetExchange || d.target_exchange);
      if (k) set.add(k);
    }
    for (const d of bankedDeals) {
      const k = monthKey(bankedDateFor(d));
      if (k) set.add(k);
    }
    return set;
  }, [forecastDeals, bankedDeals]);

  // iter-299B — Filter-aware month series. Every chart/KPI/total
  // on the page derives from this single source.
  const monthSeries = useMemo(
    () => monthSeriesFor(monthFilter, now, liveMonthKeys),
    [monthFilter, now, liveMonthKeys],
  );

  // Group forecast-eligible deals by target exchange month —
  // exchanged deals anchor on their realised exchange date.
  const dealsByMonth = useMemo(() => {
    const m = new Map();
    for (const d of forecastDeals) {
      const k = monthKey(d.targetExchange || d.target_exchange);
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(d);
    }
    return m;
  }, [forecastDeals]);

  // Chart data — 6 month forecast.
  //
  // iter-alignment · P · Expected Exchanges reconciliation
  // (2026-02-14) — `count` and `revenue` MUST now derive from
  // the SAME canonical `dealsByMonth` set so the chart bubble,
  // the banking-month summary and the deal table reconcile
  // from a single SSOT.  Previously `count` was scoped to
  // deals that had ALREADY exchanged (`isExchanged`) which
  // read as `0` on months whose deals were still forecast
  // (the entire point of an "Expected Exchanges" chart), so
  // October could show £ revenue AND a count of 0 while the
  // table listed 10 upcoming exchanges.  The fix: `count` is
  // the size of the SAME `list` the revenue sum walks, so a
  // month with zero qualifying deals still reads `0` and every
  // other month reconciles verbatim against the deal table.
  // `exchangedCount` is preserved on the row (for downstream
  // consumers that still care about the historic "already
  // exchanged" partition) but no longer drives the chart
  // bubble.
  const chartData = useMemo(() => {
    return monthSeries.map((k) => {
      const list = dealsByMonth.get(k) || [];
      const revenue = list.reduce((sum, d) => sum + _feeFor(d), 0);
      const exchangedCount = list.filter(isExchanged).length;
      return {
        key: k,
        count: list.length,
        exchangedCount,
        revenue,
        total: list.length,
      };
    });
  }, [monthSeries, dealsByMonth]);

  // KPI totals — forecast revenue across the visible horizon.
  const forecastRevenue = useMemo(
    () => chartData.reduce((s, d) => s + d.revenue, 0),
    [chartData],
  );
  const forecastTransactions = useMemo(
    () => chartData.reduce((s, d) => s + d.total, 0),
    [chartData],
  );

  // iter-299B — Filter-scoped subsets so all KPIs/totals/pressure
  // recalculate in lockstep with the dropdown.
  const monthSeriesSet = useMemo(() => new Set(monthSeries), [monthSeries]);
  const scopedForecastDeals = useMemo(() => {
    return forecastDeals.filter((d) => {
      const k = monthKey(d.targetExchange || d.target_exchange);
      return k && monthSeriesSet.has(k);
    });
  }, [forecastDeals, monthSeriesSet]);
  const scopedBankedDeals = useMemo(() => {
    return bankedDeals.filter((d) => {
      const k = monthKey(bankedDateFor(d));
      return k && monthSeriesSet.has(k);
    });
  }, [bankedDeals, monthSeriesSet]);

  // Need attention — live confidence scoring across the SCOPED
  // forecast pool. Every deal whose confidence dips under 75
  // inside the active filter surfaces here, with the at-risk total
  // summed from the same Fee+VAT envelope used everywhere else.
  const needAttention = useMemo(() => {
    // Shared Needs Attention rule (same as Deal Rooms / Sidebar / Flow Centre); confidence stays its own metric.
    const rows = scopedForecastDeals.filter(needsAttentionDeal);
    const atRisk = rows.reduce((s, d) => s + _feeFor(d), 0);
    // iter-301 — expose `dealIds` so the KPI card can deep-link.
    return { count: rows.length, atRisk, dealIds: rows.map((d) => d.id) };
  }, [scopedForecastDeals]);

  // iter-301 — Deal ID envelopes for each KPI deep-link. Counts on
  // every card match `dealIds.length` so the operator never sees a
  // mismatch between the KPI and the filtered Deal Rooms list.
  const forecastDealIds = useMemo(
    () => scopedForecastDeals.map((d) => d.id),
    [scopedForecastDeals],
  );
  const bankedDealIds = useMemo(
    () => scopedBankedDeals.map((d) => d.id),
    [scopedBankedDeals],
  );

  // iter-299 — Banked revenue. Completed deals only, scoped to the
  // active filter. The sub-line surfaces what banked in the
  // current calendar month so the operator can read momentum at a
  // glance even when the dropdown spans a wider horizon.
  const bankedRevenue = useMemo(() => {
    const total = scopedBankedDeals.reduce((s, d) => s + _feeFor(d), 0);
    const thisMonth = bankedDeals.filter((d) => {
      const k = monthKey(bankedDateFor(d));
      return k === _currentMonthKey;
    });
    const thisMonthRevenue = thisMonth.reduce((s, d) => s + _feeFor(d), 0);
    return { total, count: scopedBankedDeals.length, thisMonth: thisMonthRevenue, thisMonthCount: thisMonth.length };
  }, [scopedBankedDeals, bankedDeals, _currentMonthKey]);

  // Pre-exchange deals whose target exchange is already past: shown whatever the month filter,
  // still dated on their original target (never moved forward).
  const overdueDeals = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return forecastDeals
      .filter((d) => {
        const te = d.targetExchange || d.target_exchange;
        return te && Date.parse(String(te).slice(0, 10) + "T00:00:00") < today.getTime();
      })
      .sort((a, b) => String(a.targetExchange || a.target_exchange).localeCompare(String(b.targetExchange || b.target_exchange)));
  }, [forecastDeals]);
  const [showAllOverdue, setShowAllOverdue] = useState(false);
  const overdueRevenue = useMemo(() => overdueDeals.reduce((s, d) => s + _feeFor(d), 0), [overdueDeals]);

  // Forecast month dropdown — defaults to the first month in the
  // series that has at least one deal (or month[0]).
  const [forecastMonth, setForecastMonth] = useState(null);
  const resolvedForecastMonth = useMemo(() => {
    if (forecastMonth && monthSeries.includes(forecastMonth)) return forecastMonth;
    const firstWithDeals = monthSeries.find((k) => (dealsByMonth.get(k) || []).length > 0);
    return firstWithDeals || monthSeries[0];
  }, [forecastMonth, monthSeries, dealsByMonth]);

  const forecastMonthRows = useMemo(
    () => dealsByMonth.get(resolvedForecastMonth) || [],
    [dealsByMonth, resolvedForecastMonth],
  );
  const forecastMonthRevenue = useMemo(
    () => forecastMonthRows.reduce((s, d) => s + _feeFor(d), 0),
    [forecastMonthRows],
  );

  // Operational Pressure sourced from the SCOPED forecast pool so
  // its £-at-risk totals reconcile with the visible filter window.
  const pressureRows = useMemo(() => operationalPressureFor(scopedForecastDeals), [scopedForecastDeals]);
  const totalAtRisk = pressureRows.reduce((s, r) => s + r.atRisk, 0);

  const handleOpenDeal = useCallback((dealId) => navigate(`/deal-rooms/${dealId}`), [navigate]);

  // iter-301 — Deep-link helper. Encodes the exact deal IDs that
  // contribute to a KPI / pressure bucket into the Deal Rooms URL.
  // A non-empty list always pushes a focused subset filter; an
  // empty list still navigates so the operator sees the (empty)
  // result of their click for transparency.
  const handleOpenInDealRooms = useCallback((dealIds, label) => {
    const params = new URLSearchParams();
    if (Array.isArray(dealIds) && dealIds.length > 0) {
      params.set("ids", dealIds.join(","));
    }
    if (label) params.set("label", label);
    navigate(`/deal-rooms${params.toString() ? `?${params.toString()}` : ""}`);
  }, [navigate]);
  const handleExport = useCallback(() => {
    // Lightweight CSV export of the visible forecast rows.
    const rows = [
      ["Property", "Exchange date", "Fee (inc. VAT)", "Confidence %", "Stage"],
      ...forecastMonthRows.map((d) => {
        const prop = propertyById.get(d.propertyId) || {};
        return [
          prop.address || d.name || "",
          d.targetExchange || d.target_exchange || "",
          _feeFor(d),
          confidenceFor(d) ?? "",
          STAGE_LABEL[d.stage] || d.stage || "",
        ];
      }),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `forecast-${resolvedForecastMonth}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [forecastMonthRows, propertyById, resolvedForecastMonth]);

  return (
    <div className="min-h-screen bg-[#f7f8fb]" data-testid="forecasting-centre-page">
      <Sidebar />
      <ShellCanvas>
        <div className={WORKSPACE_SHELL_CLASS}>
          {/* Header */}
          <PageHeader
            icon={TrendingUp}
            title="Forecasting Centre"
            description="Forecast revenue and banked totals across every deal in your workspace — live from Deal Rooms."
            testid="forecasting-header"
            actions={(
              <>
                <button
                  type="button"
                  onClick={handleExport}
                  className="h-10 px-4 rounded-xl bg-white ring-1 ring-slate-200 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2 transition-colors"
                  data-testid="forecasting-export-btn"
                >
                  <Upload size={14} strokeWidth={2} />
                  Export
                </button>
                <select
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                  className="h-10 pl-9 pr-3 rounded-xl bg-white ring-1 ring-slate-200 text-[13px] font-semibold text-slate-700 appearance-none bg-no-repeat cursor-pointer hover:bg-slate-50 transition-colors"
                  style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.4' stroke-linecap='round'><rect x='3' y='4' width='18' height='18' rx='2'/><path d='M16 2v4M8 2v4M3 10h18'/></svg>\")", backgroundPosition: "12px center" }}
                  data-testid="forecasting-month-filter"
                >
                  {FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} data-testid={`forecasting-filter-${opt.value}`}>{opt.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => navigate("/today")}
                  aria-label="Notifications"
                  className="relative w-10 h-10 rounded-xl bg-white ring-1 ring-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors"
                  data-testid="forecasting-notif-btn"
                >
                  <Bell size={15} strokeWidth={2} />
                </button>
              </>
            )}
          />

          {/* KPI hero row */}
          <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8" data-testid="forecasting-kpi-row">
            <KpiCard
              testid="kpi-forecast-revenue"
              label="Forecast revenue"
              value={fmtMoneyFull(forecastRevenue)}
              accent="bg-violet-50"
              icon={TrendingUp}
              onClick={() => handleOpenInDealRooms(forecastDealIds, "Forecast revenue")}
              ariaLabel={`Open ${forecastDealIds.length} forecast ${forecastDealIds.length === 1 ? "deal" : "deals"} in Deal Rooms`}
              sub={<p className="text-[11.5px] font-semibold text-slate-500">{forecastDealIds.length} {forecastDealIds.length === 1 ? "deal" : "deals"} · Fee + VAT</p>}
            />
            <KpiCard
              testid="kpi-transactions-forecasted"
              label="Bankings forecast"
              value={forecastTransactions.toString()}
              accent="bg-emerald-50"
              icon={CalendarCheck}
              onClick={() => handleOpenInDealRooms(forecastDealIds, "Bankings forecast")}
              ariaLabel={`Open ${forecastTransactions} forecast ${forecastTransactions === 1 ? "deal" : "deals"} in Deal Rooms`}
              sub={<p className="text-[11.5px] font-semibold text-slate-500">{forecastTransactions === 1 ? "deal" : "deals"} expected to bank</p>}
            />
            <KpiCard
              testid="kpi-need-attention"
              label="Needs Attention"
              value={needAttention.count.toString()}
              accent="bg-amber-50"
              icon={AlertTriangle}
              onClick={() => handleOpenInDealRooms(needAttention.dealIds, "Needs Attention")}
              ariaLabel={`Open ${needAttention.count} ${needAttention.count === 1 ? "deal" : "deals"} needing attention in Deal Rooms`}
              sub={<p className="text-[11.5px] font-semibold text-rose-600">{fmtMoneyFull(needAttention.atRisk)} at risk</p>}
            />
            <KpiCard
              testid="kpi-banked-revenue"
              label="Banked revenue"
              value={fmtMoneyFull(bankedRevenue.total)}
              accent="bg-emerald-50"
              icon={ShieldCheck}
              onClick={() => handleOpenInDealRooms(bankedDealIds, "Banked revenue")}
              ariaLabel={`Open ${bankedDealIds.length} banked ${bankedDealIds.length === 1 ? "deal" : "deals"} in Deal Rooms`}
              // Same scoped deals as the amount: one per confirmed exchange, including later-completed deals.
              sub={<p className="text-[11.5px] font-semibold text-slate-500" data-testid="kpi-banked-revenue-count">{bankedDealIds.length} {bankedDealIds.length === 1 ? "deal" : "deals"} exchanged · Fee + VAT</p>}
            />
          </section>

          {/* Chart + Operational pressure rail.
              P1 · Match Operational Pressure card height to its
              adjacent chart (2026-08-18) — `items-stretch` makes
              both outer cards fill the row's tallest content on
              desktop/tablet (`xl:` grid).  On smaller stacks the
              single-column layout naturally returns each card to
              its content height (grid-item stretch is a no-op in
              a 1-col grid).

              iter-P2 · Dashboard column alignment (2026-02-14) —
              The row now shares the EXACT same grid axis as the
              KPI row above (`xl:grid-cols-4 gap-4`), with the
              chart spanning columns 1–3 and Operational Pressure
              living in column 4.  This guarantees the pressure
              card's left edge sits on the same vertical track as
              the "Banked revenue" KPI card directly above it —
              one intentional dashboard grid, no negative margins,
              no translate, no absolute positioning. */}
          <section className="grid grid-cols-1 min-[1440px]:grid-cols-[minmax(0,65fr)_minmax(0,35fr)] gap-4 mb-6 items-stretch" data-testid="forecast-chart-pressure-row">
            <div className="min-w-0">
              <ForecastChart data={chartData} />
            </div>

            {/* Operational Pressure — total sits 24px (mt-6) below the rows. */}
            <aside
              className="min-w-0 rounded-2xl bg-white p-6 ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] flex flex-col h-full"
              data-testid="operational-pressure"
            >
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-[15px] font-bold tracking-tight text-slate-900">Operational Pressure</h3>
              </div>
              <div>
                {pressureRows.map((r, i) => (
                  <PressureRow
                    key={r.key}
                    row={r}
                    isLast={i === pressureRows.length - 1}
                    onClick={() => handleOpenInDealRooms(r.dealIds, r.label)}
                  />
                ))}
              </div>
              <div
                className="mt-6 pt-4 border-t border-slate-100 grid grid-cols-[36px_minmax(0,1fr)_104px_16px] items-center gap-[18px]"
                data-testid="pressure-total-row"
              >
                <span className="col-span-2 text-[13px] font-bold text-slate-900">Total at risk</span>
                <span className="text-[14px] font-bold text-rose-600 tabular-nums text-right" data-testid="pressure-total-at-risk">{fmtMoneyFull(totalAtRisk)}</span>
                <span aria-hidden="true" className="w-4" />
              </div>
            </aside>
          </section>

          {overdueDeals.length > 0 && (
            <section className="rounded-2xl bg-white ring-1 ring-rose-200/80 overflow-hidden mb-6" data-testid="forecast-overdue-section">
              <div className="flex items-center gap-6 flex-wrap px-5 py-4 border-b border-slate-100">
                <h3 className="text-[15px] font-bold tracking-tight text-rose-700">Overdue exchange targets</h3>
                <span className="text-[13px] text-slate-500">
                  <span className="font-bold text-slate-900 tabular-nums" data-testid="forecast-overdue-count">{overdueDeals.length}</span> deals ·{" "}
                  <span className="font-bold text-slate-900 tabular-nums" data-testid="forecast-overdue-revenue">{fmtMoneyFull(overdueRevenue)}</span>
                </span>
                <span className="text-[12px] text-slate-500">Target date has passed without exchange. Shown on the original target; update the target in the Deal Room.</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="forecast-overdue-table">
                  <tbody>
                    {(showAllOverdue ? overdueDeals : overdueDeals.slice(0, 5)).map((d, i) => (
                      <ForecastRow key={d.id} deal={d} propertyById={propertyById} onOpen={handleOpenDeal} index={i} prefix="forecast-overdue-row" overdue />
                    ))}
                  </tbody>
                </table>
              </div>
              {overdueDeals.length > 5 && (
                <button
                  type="button"
                  onClick={() => setShowAllOverdue((v) => !v)}
                  className="w-full px-5 py-3 border-t border-slate-100 text-left text-[12.5px] font-semibold text-violet-700 hover:text-violet-900"
                  data-testid="forecast-overdue-toggle"
                >
                  {showAllOverdue ? "Show fewer" : `Show all ${overdueDeals.length} overdue deals`}
                </button>
              )}
            </section>
          )}

          {/* Forecast month dropdown + table */}
          <section className="rounded-2xl bg-white ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden" data-testid="forecast-table-section">
            <div className="flex items-center gap-6 flex-wrap px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <span className="text-[12.5px] font-semibold text-slate-500">Banking month</span>
                <div className="relative">
                  <select
                    value={resolvedForecastMonth}
                    onChange={(e) => setForecastMonth(e.target.value)}
                    className="h-9 pl-3 pr-9 rounded-lg bg-white ring-1 ring-slate-200 text-[13px] font-semibold text-slate-900 appearance-none cursor-pointer hover:bg-slate-50 transition-colors"
                    data-testid="forecast-month-dropdown"
                  >
                    {monthSeries.map((k) => (
                      <option key={k} value={k}>{monthLong(k)}</option>
                    ))}
                  </select>
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">▾</span>
                </div>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-[15px] font-bold text-slate-900 tabular-nums" data-testid="forecast-month-count">{forecastMonthRows.length}</span>
                <span className="text-[12.5px] text-slate-500">{forecastMonthRows.length === 1 ? "exchange" : "exchanges"}</span>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-[15px] font-bold text-slate-900 tabular-nums" data-testid="forecast-month-revenue">{fmtMoneyFull(forecastMonthRevenue)}</span>
                <span className="text-[12.5px] text-slate-500">expected to bank</span>
              </div>
              <Link
                to="/deal-rooms"
                className="ml-auto text-[12.5px] font-semibold text-violet-700 hover:text-violet-900"
                data-testid="forecast-view-all"
              >
                View all
              </Link>
            </div>

            {forecastMonthRows.length === 0 ? (
              <div className="px-5 py-12 text-center" data-testid="forecast-table-empty">
                <p className="text-[13px] font-semibold text-slate-700">No exchanges forecast for {monthLong(resolvedForecastMonth)}</p>
                <p className="text-[12px] text-slate-500 mt-1">Pick another month or add a target exchange date to a deal.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="forecast-table">
                  <thead>
                    <tr className="text-[11px] font-bold tracking-wide uppercase text-slate-400">
                      <th className="text-left pl-4 pr-3 py-3">Property</th>
                      <th className="text-left px-3 py-3">Exchange date</th>
                      <th className="text-left px-3 py-3">Fee (inc. VAT)</th>
                      <th className="text-left px-3 py-3">Confidence</th>
                      <th className="text-left px-3 py-3">Stage</th>
                      <th className="text-left px-3 pr-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forecastMonthRows.map((d, i) => (
                      <ForecastRow key={d.id} deal={d} propertyById={propertyById} onOpen={handleOpenDeal} index={i} />
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
                  <Link
                    to="/deal-rooms"
                    className="text-[12.5px] font-semibold text-violet-700 hover:text-violet-900"
                    data-testid="forecast-view-all-properties"
                  >
                    View all {forecastMonthRows.length} {forecastMonthRows.length === 1 ? "property" : "properties"} →
                  </Link>
                </div>
              </div>
            )}
          </section>
        </div>
      </ShellCanvas>
    </div>
  );
};

export default ForecastingCentrePage;
